import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Avatar, type AvatarUser } from "@/components/ui/Avatar";
import { Icon } from "@/components/ui/Icon";
import { MemoMarkdown } from "@/components/memo/MemoMarkdown";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { CountPill } from "@/components/memo/ui/CountPill";
import { entryDisplayKind } from "@/lib/memoCarry";
import { uploadForEditor } from "@/lib/fileEmbed";
import { deriveAvatarColor, deriveInitials } from "@/lib/colors";
import { formatClosureDate } from "@/lib/format";
import { BRAND } from "@/lib/tokens";
import { t } from "@/lib/strings";
import { attributionFor } from "@/lib/memoAttribution";
import { dismissToast, showToast } from "@/lib/toast";
import type { MemoMutations } from "@/hooks/useMemo";
import type { MemoEntry, MentionItem, WorkspaceMember } from "@/lib/types";

interface Props {
  entry: MemoEntry;
  mut: MemoMutations;
  canEdit: boolean;
  members: WorkspaceMember[];
  editing: boolean;
  /** Show the task-oriented placeholder (opened via "+ Aufgabe"). */
  taskHint?: boolean;
  /** This entry belongs to an EARLIER meeting, so its text and its existence are
   * protected. State changes (done, Frist, Zurückziehen, move) stay allowed.
   * Applies in EVERY mode now, not just presentation, and is enforced server-side
   * by memo_access.require_can_edit_history — no longer only a guard-rail. */
  historyLocked?: boolean;
  /** Series editors/admins can unlock while presenting; a participant cannot, so
   *  the "why did nothing happen" toast must not point at a button they lack. */
  mayEditHistory?: boolean;
  onStartEdit: () => void;
  onStopEdit: () => void;
  /** Backspace on an empty point: the parent deletes it and focuses the previous one. Return
   *  the delete's promise, so the row knows when it is leaving and when it is staying. */
  onEmptyBackspace: () => void | Promise<void>;
  /** "In Abschnitt verschieben" control, rendered in the hover actions. */
  moveMenu?: React.ReactNode;
  /** A finalized Protokoll shows no attribution: the room has seen it, so the line is no
   *  longer "something that appeared" — it is minutes. */
  finalized?: boolean;
  /** Fired when the retire toggle is used, alongside the mutation — lets the block
   *  keep a just-closed point visible while presenting a draft. */
  onToggleRetire?: (entryId: string, retired: boolean) => void;
}

function toAvatarUser(m: WorkspaceMember): AvatarUser {
  const name = m.display_name ?? m.email;
  return {
    id: m.user_id,
    display_name: name,
    initials: deriveInitials(name),
    avatar_color: deriveAvatarColor(m.user_id),
    username: m.username,
  };
}

function fmtDate(iso: string): string {
  const [, mo, d] = iso.split("-");
  return `${d}.${mo}.`;
}

export function MemoEntryRow({
  entry,
  mut,
  canEdit,
  members,
  editing: editingProp,
  taskHint,
  historyLocked = false,
  finalized = false,
  mayEditHistory = false,
  onStartEdit,
  onStopEdit,
  onEmptyBackspace,
  moveMenu,
  onToggleRetire,
}: Props) {
  const kind = entryDisplayKind(entry);
  const isTask = kind === "open" || kind === "done";
  // Sub-item rollup of the linked Aufgaben row. The fields are optional on the
  // wire (additive, no migration), so an older payload degrades to 0 → no pill.
  const subtaskTotal = entry.subtask_total ?? 0;
  const subtaskDone = entry.subtask_done ?? 0;
  // If the note's body IS a list already (starts with a bullet/number), it has
  // its own markers — drop the entry's gutter dot so there's no double bullet.
  // Prose (or prose-then-bullets) keeps the dot on its first line.
  const firstBodyLine = entry.body.split("\n").find((l) => l.trim()) ?? "";
  const startsWithList = /^\s*([-*+]|\d+[.)])\s/.test(firstBodyLine);
  // Text + existence are protected on an earlier meeting's entry; its STATE
  // (done, Frist, Zurueckziehen, move) stays editable.
  const canEditText = canEdit && !historyLocked;

  // Who added this line — null unless it was written in THIS meeting, in a draft. Both rules
  // live in the pure lib, because getting them wrong is not a cosmetic bug: carry-forward
  // stamps `created_by` with the person who created the DRAFT, so a naive read would credit
  // them for a colleague's line from weeks ago.
  const attributedToId = attributionFor(entry, { finalized });
  const attributedTo = attributedToId
    ? (members.find((m) => m.user_id === attributedToId) ?? null)
    : null;
  const attributionTitle = attributedTo
    ? [attributedTo.display_name ?? attributedTo.username ?? attributedTo.email, entry.log_date]
        .filter(Boolean)
        .join(" · ")
    : undefined;
  // A locked entry is NEVER in edit mode, whoever set `editing`. Guarding only
  // click-to-edit was not enough: the empty-backspace merge focuses the PREVIOUS
  // entry, which mounted its editor and let a blur silently rewrite protected text.
  const editing = editingProp && !historyLocked;
  const [draft, setDraft] = useState(entry.body);
  const [dueOpen, setDueOpen] = useState(false);
  // Draft for the Frist input: nothing is written until Enter or the ✓ (see the input below).
  const [dueDraft, setDueDraft] = useState("");
  const [solving, setSolving] = useState(false);
  const [solution, setSolution] = useState("");
  // A recorded Lösung can be long (pasted steps, code). It renders as a wrapping
  // block clamped to 3 lines, with a toggle to reveal the rest — never the old
  // single nowrap line that ran off the whole protocol. The toggle only appears
  // when the text actually exceeds the clamp (measured against the clamped box).
  const [solutionExpanded, setSolutionExpanded] = useState(false);
  const [solutionOverflows, setSolutionOverflows] = useState(false);
  const solutionRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (solutionExpanded) return;
    const el = solutionRef.current;
    if (!el) return;
    setSolutionOverflows(el.scrollHeight - el.clientHeight > 1);
  }, [entry.task_solution, kind, solutionExpanded]);

  // Dedupe against the last body actually SENT (not `entry.body`, which lags
  // until the mutation round-trips). Ctrl+Enter fires onFinish → commit +
  // onStopEdit, which unmounts the editor and can fire a trailing blur →
  // commit again with the same body; without this ref both requests go out and
  // sync_entry_task races into a second board row.
  // Seed the dedupe baseline from the SAME trailing-trimmed form the editor
  // loads (it strips a trailing blank block on open), so a no-edit open→blur of
  // a legacy entry whose body ends in whitespace doesn't fire a redundant patch.
  const committedRef = useRef(entry.body.replace(/\s+$/, ""));
  // The delete branch of exitEditor needs its own guard: commit() dedupes via
  // committedRef, but a ghost-row delete has no body to compare. Ctrl+Enter on an
  // untouched "+ Eintrag" calls exitEditor once via onFinish, and the unmount's
  // trailing blur calls it again — without this the second DELETE 404s on an id
  // that is already gone.
  const deletedRef = useRef(false);
  // 🔴 Set while Backspace on an empty point is deleting this row. Chrome and Edge fire `blur`
  // on a focused element that leaves the page, and the editor's blur handler is still attached
  // when the row unmounts, so exitEditor ran once more for a row that was already gone: a fresh
  // point sent a second DELETE, an erased one sent PATCH {"body": ""}, both answered 404 with a
  // red "konnte nicht … werden" toast (jsdom fires no such blur, so the suite never saw it).
  // Reset when the delete is refused: the row stays, and its next edit must be saved.
  const leavingRef = useRef(false);
  const wasEditingRef = useRef(editing);
  // 🔴 Has this row EVER held committed content? `patchEntry` is not optimistic
  // (`useMemo.ts` only invalidates on success), so for one round trip after a
  // commit the `entry.body` prop is still the stale empty string — and the row
  // renders its grey placeholder, which invites the writer to click it. The
  // re-baseline effect below then seeds the draft AND the dedupe ref from that
  // same stale "", so the next blur reports a blank document over an entry whose
  // body looks empty, and the ghost-cleanup would DELETE a bullet that has text.
  // This ref is the row's memory across that window; it is deliberately NOT reset
  // by the re-baseline. Set only on a COMMITTED body, never on a draft, so
  // type-then-erase-then-leave still cleans up the ghost (bde74bc).
  const everHadContentRef = useRef(entry.body.trim() !== "");
  useEffect(() => {
    if (entry.body.trim() !== "") everHadContentRef.current = true;
  }, [entry.body]);

  // Re-baseline the draft AND the dedupe ref only when an edit session OPENS
  // (editing false→true). We deliberately do NOT reset on every `entry.body`
  // change while editing: a mid-session collaborator edit must not clobber the
  // user's in-progress typing, and — crucially — re-baselining on session open
  // releases the dedupe so a legitimate re-type of a body whose earlier patch
  // failed to land (entry.body still stale) is sent again instead of swallowed.
  // Within one Enter→(commit)→(blur commit) sequence neither `editing` nor
  // `entry.body` changes, so no re-baseline happens and the blur stays deduped.
  useEffect(() => {
    if (editing && !wasEditingRef.current) {
      setDraft(entry.body);
      committedRef.current = entry.body.replace(/\s+$/, "");
      deletedRef.current = false;
    }
    wasEditingRef.current = editing;
  }, [editing, entry.body]);

  const mentionItems: MentionItem[] = members.map((m) => ({
    id: m.user_id,
    label: m.display_name ?? m.username ?? m.email,
  }));
  const assignee = members.find((m) => m.user_id === entry.assignee_user_id) ?? null;
  const assigneeName = assignee
    ? (assignee.display_name ?? assignee.username ?? assignee.email)
    : entry.assignee_user_id
      ? "?"
      : null;

  const commit = (body: string) => {
    if (body !== committedRef.current) {
      committedRef.current = body;
      if (body.trim() !== "") everHadContentRef.current = true;
      mut.patchEntry.mutate({ entryId: entry.id, body });
    }
  };

  // The single way an edit session ends, shared by blur and Ctrl/Cmd+Enter.
  //
  // A pristine, never-filled row that ends with no content (e.g. "+ Eintrag" /
  // "+ Aufgabe" then click away, or Ctrl+Enter without typing) is dropped rather
  // than left behind as an empty placeholder ghost — otherwise empties pile up.
  // Only auto-removes rows that never had content (entry.body still empty) and
  // aren't a task (no linked board row).
  //
  // 🔴 `blank` is decided by the editor from its DOCUMENT (`lib/editorBlank`), not
  // from `md`. Testing the markdown missed an EMPTY BULLET: tiptap-markdown
  // serializes it to "-", so the guard saw content and the ghost survived. It also
  // missed the lone "@" that the "+ Aufgabe" prime types for the writer.
  const exitEditor = (md: string, blank: boolean) => {
    // The row's own delete is under way: nothing may follow it (see leavingRef).
    if (leavingRef.current) {
      onStopEdit();
      return;
    }
    if (blank && !everHadContentRef.current && entry.body.trim() === "" && entry.row_id === null) {
      if (!deletedRef.current) {
        deletedRef.current = true;
        mut.deleteEntry.mutate(entry.id);
      }
      onStopEdit();
      return;
    }
    commit(md);
    onStopEdit();
  };

  const handleEmptyBackspace = () => {
    leavingRef.current = true;
    // The catch also settles the rejection: the hook already reports a refused delete.
    void Promise.resolve(onEmptyBackspace()).catch(() => {
      leavingRef.current = false;
    });
  };

  const handleDelete = () => {
    // 🔴 `log_date` is part of what undo must put back, and it is NOT optional any more.
    // The running log orders CHRONOLOGICALLY (lib/memoLogOrder.ts), so replaying `position`
    // alone no longer restores anything: `add_entry` stamps a fresh entry with the memo's
    // meeting_date, so undoing the delete of a carried-forward line dated 20.04 inside a 13.07
    // meeting would resurrect it into TODAY's date group — a line the room never wrote today,
    // sitting under today's divider. Undo must return the line it deleted, not a copy of it.
    const { id, body, block_id, position, due_date, task_done, task_solution, retired, log_date } =
      entry;
    // 🔴 The undo toast goes up at once, before the DELETE answers. When the delete FAILS the point
    // is still there, and "Rückgängig" used to re-create it anyway: a duplicate line, and for a
    // to-do a second Aufgaben row and a second assignment mail. So the undo waits for the answer
    // and does nothing unless the delete went through, and a refused delete takes its toast back
    // (the hook's error toast says what happened). Never rejects: `then` maps both outcomes.
    const deleted = mut.deleteEntry.mutateAsync(id).then(
      () => true,
      () => false,
    );
    const toastId = showToast(t.entryDeleted, "info", 5000, {
      label: t.undo,
      // Async, but self-contained: the try/catch means it never rejects, so the
      // Toaster (which fires it fire-and-forget) can't leak an unhandled rejection.
      onClick: async () => {
        if (!(await deleted)) return;
        try {
          const created = await mut.addEntry.mutateAsync({
            blockId: block_id,
            body,
            logDate: log_date,
          });
          // `quiet` on every replay step that toasts on its own: the catch below reports a
          // failed undo ONCE ("Wiederherstellen fehlgeschlagen."), not twice.
          await mut.patchEntry.mutateAsync({ entryId: created.id, position, quiet: true });
          // Faithfully replay the deleted bullet's task state onto the new row
          // so a done/scheduled task doesn't come back as a fresh open one.
          if (due_date) await mut.setDue.mutateAsync({ entryId: created.id, due_date });
          if (task_done)
            await mut.completeTask.mutateAsync({ entryId: created.id, solution: task_solution });
          if (retired)
            await mut.retireEntry.mutateAsync({ entryId: created.id, retired: true, quiet: true });
        } catch {
          showToast(t.entryRestoreFailed, "error");
        }
      },
    });
    void deleted.then((ok) => {
      if (!ok) dismissToast(toastId);
    });
  };

  // Non-editing display of the entry body (title). Shared verbatim between
  // the plain-note layout and the task card layout below — click-to-edit
  // behavior is identical in both.
  const displayBody = (
    <div
      onClick={() => {
        // Silent no-ops send people hunting: say WHY the click did nothing.
        if (canEdit && historyLocked) {
          showToast(mayEditHistory ? t.memoPresentHistoryLocked : t.memoHistoryLockedNoRight);
          return;
        }
        if (canEditText && kind !== "retired") onStartEdit();
      }}
      style={{ cursor: canEditText && kind !== "retired" ? "text" : "default", fontSize: 13.5 }}
    >
      {entry.body.trim() ? (
        <MemoMarkdown markdown={entry.body} />
      ) : (
        <span style={{ color: "var(--ink-4)" }}>{t.memoEntryPlaceholder}</span>
      )}
    </div>
  );

  // A Frist that has slipped past today (done tasks never read as overdue).
  const todayIso = new Date().toISOString().slice(0, 10);
  const overdue = entry.due_date ? entry.due_date < todayIso && kind !== "done" : false;

  // Leading checkbox — the task card's primary done control. Open = empty
  // square; done = filled green with a white check. Clicking an open box opens
  // the inline Lösung capture (preserving task_solution); clicking a done box
  // reopens. Retired / read-only → non-interactive.
  const checkboxBase: React.CSSProperties = {
    width: 18,
    height: 18,
    borderRadius: 5,
    flexShrink: 0,
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 1,
    padding: 0,
  };
  const doneBox: React.CSSProperties = {
    ...checkboxBase,
    background: DONE_GREEN,
    border: `2px solid ${DONE_GREEN}`,
  };
  const openBox: React.CSSProperties = {
    ...checkboxBase,
    background: "transparent",
    border: "2px solid var(--hairline-strong)",
  };
  const doneCheck = <Icon name="check" size={12} color="#fff" />;
  const doneCheckbox =
    kind === "done" ? (
      canEdit ? (
        <button
          type="button"
          title={t.memoTaskReopen}
          aria-label={t.memoTaskReopen}
          onClick={() => mut.reopenTask.mutate(entry.id)}
          style={{ ...doneBox, cursor: "pointer" }}
        >
          {doneCheck}
        </button>
      ) : (
        <span aria-label={t.memoTaskDone} style={doneBox}>
          {doneCheck}
        </span>
      )
    ) : canEdit ? (
      <button
        type="button"
        title={t.memoTaskDone}
        aria-label={t.memoTaskDone}
        onClick={() => setSolving(true)}
        style={{ ...openBox, cursor: "pointer" }}
      />
    ) : (
      <span aria-label={t.memoTaskDone} style={openBox} />
    );

  // Chip row: assignee · Erledigt · Frist — one uniform pill language, shared
  // verbatim between the inline (editing) layout and the task card. The done
  // toggle itself lives in the leading checkbox, not here.
  const chipRow = isTask ? (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginTop: 8,
        flexWrap: "wrap",
      }}
    >
      {/* Assignee chip — larger avatar so 3-letter codes (e.g. "AKN") fit. */}
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1.3,
          whiteSpace: "nowrap",
          color: "var(--ink-1)",
          background: "var(--surface-soft)",
          border: "1px solid var(--hairline)",
          borderRadius: 8,
          padding: "3px 9px",
        }}
      >
        {assignee ? (
          <Avatar user={toAvatarUser(assignee)} size={20} />
        ) : (
          <Icon name="person" size={13} color="var(--accent)" />
        )}
        {assigneeName}
      </span>

      {/* Sub-item rollup — "2 von 5 erledigt" — ONLY when the linked board row was
          actually broken down. No sub-items ⇒ subtask_total is 0 ⇒ nothing renders,
          so a plain to-do looks exactly as it always did (no "0 von 0", no empty
          pill). Deliberately just a count: the sub-assignees stay off the minutes. */}
      {subtaskTotal > 0 ? (
        <CountPill tone="neutral">
          <Icon name="checkbox" size={12} />
          {t.memoSubtaskRollup
            .replace("{done}", String(subtaskDone))
            .replace("{total}", String(subtaskTotal))}
        </CountPill>
      ) : null}

      {/* Erledigt status chip — a compact marker. The Lösung text (which can be a
          long paste) is NOT crammed in here any more: it rendered as one nowrap
          line that ran past the whole protocol. It now has its own wrapping block
          (solutionBlock) below the chip row. */}
      {kind === "done" ? (
        <CountPill tone="done">
          <Icon name="check" size={13} />
          {t.memoTaskDone}
        </CountPill>
      ) : null}

      {/* Frist: resting state is the uniform pill; picker + remove stay live. */}
      {entry.due_date ? (
        <CountPill tone={overdue ? "overdue" : "neutral"}>
          <Icon name="date" size={12} />
          bis {fmtDate(entry.due_date)}
          {canEdit ? (
            <button
              type="button"
              title="Frist entfernen"
              onClick={() => mut.setDue.mutate({ entryId: entry.id, due_date: null })}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                marginLeft: -1,
                lineHeight: 0,
                color: "inherit",
              }}
            >
              <Icon name="x" size={11} />
            </button>
          ) : null}
        </CountPill>
      ) : canEdit ? (
        dueOpen ? (
          // 🔴 DRAFT-ONLY. This input used to commit AND close on every `onChange` — and a
          // native date input fires one per SEGMENT. Typing 30 → 09 → the first digit of the
          // year briefly forms `0002-09-30`, which is a truthy, "valid" date: it was saved and
          // the picker slammed shut before the year could be finished. So a Frist could not be
          // set at all beyond the current month, and the ones that got through carried a
          // garbage year. The same mis-fire is why the native calendar's month arrows closed it
          // (the reported "it exits every time I click to September").
          //
          // Neither typing nor the month arrows commit now: Enter or the ✓ commits, Esc cancels.
          // There is deliberately NO onBlur — a blur fires the instant you reach for the ✓.
          // This is the pattern the log-date divider already uses (MemoBlockView), whose comment
          // says exactly this. It was never applied here.
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <input
              type="date"
              autoFocus
              value={dueDraft}
              onChange={(e) => setDueDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && dueDraft) {
                  mut.setDue.mutate({ entryId: entry.id, due_date: dueDraft });
                  setDueOpen(false);
                } else if (e.key === "Escape") {
                  setDueOpen(false);
                }
              }}
              style={{
                fontSize: 12,
                padding: "3px 9px",
                borderRadius: 8,
                border: "1px solid var(--hairline)",
                background: "var(--surface)",
                color: "var(--ink-1)",
              }}
            />
            <button
              type="button"
              title="Übernehmen"
              disabled={!dueDraft}
              onClick={() => {
                if (dueDraft) {
                  mut.setDue.mutate({ entryId: entry.id, due_date: dueDraft });
                  setDueOpen(false);
                }
              }}
              style={{
                background: "transparent",
                border: "none",
                cursor: dueDraft ? "pointer" : "default",
                padding: 2,
                lineHeight: 0,
              }}
            >
              <Icon name="check" size={15} color="var(--ink-2)" />
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => {
              setDueDraft(entry.due_date ?? "");
              setDueOpen(true);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.3,
              color: "var(--ink-4)",
              background: "none",
              border: "1px dashed var(--hairline)",
              borderRadius: 8,
              padding: "3px 9px",
              cursor: "pointer",
            }}
          >
            <Icon name="date" size={12} color="var(--ink-4)" />
            Frist
          </button>
        )
      ) : null}
    </div>
  ) : null;

  // Inline Lösung capture, opened by the checkbox. Optional note → completeTask.
  const solutionInput =
    isTask && canEdit && solving && kind !== "done" ? (
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
        <input
          autoFocus
          value={solution}
          onChange={(e) => setSolution(e.target.value)}
          placeholder={t.memoSolutionPlaceholder}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              mut.completeTask.mutate({ entryId: entry.id, solution: solution || null });
              setSolving(false);
            } else if (e.key === "Escape") {
              setSolving(false);
            }
          }}
          style={{
            fontSize: 12,
            padding: "3px 9px",
            borderRadius: 8,
            border: "1px solid var(--hairline)",
            background: "var(--surface)",
            color: "var(--ink-1)",
            minWidth: 180,
          }}
        />
        <button
          type="button"
          onClick={() => {
            mut.completeTask.mutate({ entryId: entry.id, solution: solution || null });
            setSolving(false);
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: DONE_GREEN,
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "3px 9px",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          <Icon name="check" size={12} color="#fff" />
          {t.memoTaskDone}
        </button>
      </div>
    ) : null;

  // Recorded Lösung: a readable, wrapping block clamped to 3 lines with a toggle,
  // NOT the old nowrap pill that overflowed the protocol. pre-wrap keeps pasted
  // line breaks; overflow-wrap:anywhere keeps a long unbroken token in bounds.
  const solutionBlock =
    kind === "done" && entry.task_solution ? (
      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 11.5, fontWeight: 600, color: "var(--ink-3)", marginBottom: 2 }}>
          {t.memoSolutionLabel}
        </div>
        <div
          ref={solutionRef}
          style={{
            fontSize: 12.5,
            lineHeight: 1.45,
            color: "var(--ink-2)",
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
            ...(solutionExpanded
              ? {}
              : {
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }),
          }}
        >
          {entry.task_solution}
        </div>
        {solutionOverflows || solutionExpanded ? (
          <button
            type="button"
            onClick={() => setSolutionExpanded((v) => !v)}
            style={{
              marginTop: 3,
              padding: 0,
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 11.5,
              fontWeight: 600,
              color: "var(--ink-3)",
            }}
          >
            {solutionExpanded ? t.memoSolutionCollapse : t.memoSolutionExpand}
          </button>
        ) : null}
      </div>
    ) : null;

  // Closure-date badge: a RETIRED entry (kind === "retired") is never `isTask`
  // (entryDisplayKind checks `retired` first, before assignee/task_done), so it
  // never reaches `chipRow` — that block is gated on `isTask` and only renders
  // for open/done to-dos. A retired line falls through to the plain-note layout
  // below instead, so the badge lives here, rendered unconditionally after that
  // layout's branches, rather than as a sibling of the green "Erledigt" pill.
  // Only shown once the memo is finalized: a draft's retired lines are still
  // being worked, not yet minutes.
  const closureBadge =
    kind === "retired" && entry.retired_at && (finalized || entry.is_historical) ? (
      <div style={{ marginTop: 6 }}>
        <CountPill tone="neutral">
          <Icon name="check" size={12} />
          abgeschlossen {formatClosureDate(entry.retired_at)}
        </CountPill>
      </div>
    ) : null;

  return (
    <div
      // 🔴 While editing, the row's own chrome must not steal focus. The editor COMMITS AND
      // CLOSES on blur (exitEditor), so a mousedown that lands anywhere other than the
      // contenteditable — the 5px row padding, the bullet-dot gutter, the gap between the
      // dot and the text — used to shut the editor mid-edit. Same defect as the toolbar
      // strip above the text (RichTextEditor), and the same fix: never let focus leave.
      //
      // Scoped to `editing` so click-to-edit still works when the row is NOT in edit mode:
      // preventDefault there would suppress the focus the editor needs on mount.
      // The `closest` escape hatch keeps the row's real controls (the done checkbox, the
      // retire/delete buttons, the Frist input) clickable — they own their own focus.
      onMouseDown={
        editing
          ? (e) => {
              const el = e.target as HTMLElement | null;
              if (!el?.closest("button, a[href], input, select, [contenteditable]")) {
                e.preventDefault();
              }
            }
          : undefined
      }
      style={{
        display: "flex",
        gap: 10,
        // No per-point focus styling: presentation focus/dim now lives on the
        // enclosing FOCUS UNIT (Zwischenabschnitt) container, not on each point.
        padding: "5px 0",
        opacity: kind === "retired" ? 0.5 : 1,
        transition: "opacity 140ms ease, background 140ms ease",
      }}
      id={`memo-entry-${entry.id}`}
      className="flow-memo-entry"
    >
      {/* Gutter: a calm bullet dot, aligned to the first line. Dropped for
          task cards (not editing) — the card itself is the visual unit. */}
      {isTask && !editing ? null : (
        // Center the dot on the first text line rather than pinning it with a
        // magic top offset: a line-box that matches the entry font size (13.5)
        // + line-height (1.5), shifted down by MarkdownContent's first-paragraph
        // top margin (4px) so the dot sits on the line's optical center.
        <span
          style={{
            display: "flex",
            alignItems: "center",
            height: "1.5em",
            width: 5,
            fontSize: 13.5,
            marginTop: 4,
            flexShrink: 0,
          }}
        >
          {/* 🔴 NEVER while the editor is open. The dot is a DISPLAY affordance: it stands in
              for the bullet that the rendered markdown does not draw. Inside the editor,
              TipTap draws its own list markers AND puts a toolbar strip at the top of the
              entry — so the dot (which centres itself on the entry's FIRST LINE BOX) landed
              next to the TOOLBAR, level with the B / I / <> buttons, and on a list body it was
              a THIRD bullet next to two real ones. It read as a rendering fault, which is
              exactly what colleagues reported.

              The condition used to be `startsWithList && !editing`, i.e. "hide it for a list —
              unless you are editing", which is backwards: editing is when the editor owns the
              visual entirely. The enclosing span still reserves its 5px, so opening the editor
              does not shift the text sideways. */}
          {startsWithList || editing ? null : (
            <span
              data-memo-bullet
              style={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                background: isTask ? BRAND.blue : "var(--ink-3)",
              }}
            />
          )}
        </span>
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        {isTask && !editing ? (
          // Task entry, not editing: title + task line together in a card —
          // the visual unit that distinguishes a task from a plain note.
          <div
            style={{
              // Calm soft-grey card. Presentation focus is handled by the unit
              // container, so the card carries no focus ring of its own.
              background: "var(--surface-soft)",
              border: "1px solid var(--hairline)",
              borderRadius: 10,
              padding: "13px 15px",
              transition: "background 140ms ease",
            }}
          >
            <div style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
              {doneCheckbox}
              <div style={{ flex: 1, minWidth: 0 }}>
                {displayBody}
                {chipRow}
                {solutionBlock}
                {solutionInput}
              </div>
            </div>
          </div>
        ) : (
          <>
            {editing ? (
              <RichTextEditor
                // Blank lines survive saving; empty bullets are not stored.
                variant="memo"
                value={draft}
                onChange={setDraft}
                mentionItems={mentionItems}
                placeholder={taskHint ? t.memoTaskPlaceholder : t.memoEntryPlaceholder}
                autoFocus
                autoMention={taskHint}
                onBlur={exitEditor}
                // Ctrl/Cmd+Enter finishes the entry. It routes through the SAME
                // handler as blur, not a bare commit(): otherwise "+ Eintrag" →
                // Ctrl+Enter on an untouched row would leave the empty ghost that
                // bde74bc removed. Both now carry (markdown, blank) read off the
                // live document, so neither depends on `draft` having caught up.
                onFinish={exitEditor}
                onEmptyBackspace={handleEmptyBackspace}
                onUploadFile={uploadForEditor}
              />
            ) : (
              displayBody
            )}
            {chipRow}
            {solutionBlock}
          </>
        )}
        {closureBadge}
      </div>

      {/* 🔴 WHO PUT THIS HERE. A draft fills up between meetings and, without this, nobody can
          tell who added what — a line that ought to be discussed reads as if it had always
          been part of the document. That is the "ghost content" problem.
          Muted at rest so it never competes with the text; the tooltip carries the name and
          the line's date. Only ever rendered for lines written in THIS meeting and only while
          the memo is a draft — see lib/memoAttribution, where both rules live and are tested. */}
      {attributedTo ? (
        <div
          className="flow-memo-attribution"
          title={attributionTitle}
          style={{ flexShrink: 0, alignSelf: "flex-start", marginTop: 3, opacity: 0.45 }}
        >
          <Avatar user={toAvatarUser(attributedTo)} size={16} />
        </div>
      ) : null}

      {/* Hover actions: retire + delete, top-aligned so the gutter stays calm. */}
      {canEdit && !entry.is_historical ? (
        <div className="flow-memo-actions" style={{ display: "flex", gap: 2, flexShrink: 0 }}>
          {moveMenu}
          <button
            type="button"
            title={entry.retired ? t.memoUnretire : t.memoRetire}
            onClick={() => {
              const next = !entry.retired;
              mut.retireEntry.mutate({ entryId: entry.id, retired: next });
              onToggleRetire?.(entry.id, next);
            }}
            style={iconBtn}
          >
            <Icon name={entry.retired ? "refresh" : "check"} size={14} color="var(--ink-4)" />
          </button>
          {historyLocked ? null : (
            <button type="button" title="Löschen" onClick={handleDelete} style={iconBtn}>
              <Icon name="trash" size={13} color="var(--ink-4)" />
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}

// The app's success green — a fixed hue (like the STATUS_PALETTE tints), so the
// solid green checkbox + white check stay legible in both themes without pairing
// a theme-flipping var with a hardcoded ground.
const DONE_GREEN = "#2f7d54";

const iconBtn: React.CSSProperties = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  padding: 3,
  lineHeight: 0,
};
